/**
 * /network/inbox/:conversationId — Full-height DM thread
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, ArrowLeft, Loader2, ChevronRight, Users, Trophy, Sparkles, Network } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const fmtTime = iso => iso ? new Date(iso).toLocaleTimeString('en-US',{ hour:'2-digit', minute:'2-digit' }) : '';
const fmtDate = iso => {
  if (!iso) return '';
  const d = new Date(iso), now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return 'Today';
  const yest = new Date(now); yest.setDate(yest.getDate()-1);
  if (d.toDateString() === yest.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US',{ weekday:'short', month:'short', day:'numeric' });
};

const getMyId = () => {
  try {
    const token = localStorage.getItem('storycraft_token');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1])).user_id || null;
  } catch { return null; }
};

function groupByDay(messages) {
  const groups = [];
  let curDate = null;
  messages.forEach(m => {
    const d = m.created_at ? new Date(m.created_at).toDateString() : 'Unknown';
    if (d !== curDate) { curDate = d; groups.push({ type:'date', label: fmtDate(m.created_at) }); }
    groups.push({ type:'msg', ...m });
  });
  return groups;
}

export const NetworkThreadPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages]   = useState([]);
  const [text, setText]           = useState('');
  const [loading, setLoading]     = useState(true);
  const [sending, setSending]     = useState(false);
  const [otherName, setOtherName] = useState('');
  const [otherHandle, setOtherHandle] = useState('');
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const myId = getMyId();

  const load = useCallback(async () => {
    try {
      const [msgRes, convRes] = await Promise.all([
        api.get(`/network/conversations/${conversationId}/messages`),
        api.get('/network/conversations').catch(() => ({ data:{ conversations:[] } })),
      ]);
      setMessages(msgRes.data.messages || []);
      const conv = (convRes.data.conversations||[]).find(c => c.id === conversationId);
      if (conv) { setOtherName(conv.other_display_name||conv.other_handle||''); setOtherHandle(conv.other_handle||''); }
    } catch { /* silent on poll */ }
    finally { setLoading(false); }
  }, [conversationId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/network/conversations/${conversationId}/messages`, { content: text.trim() });
      setText(''); load();
      textareaRef.current?.focus();
    } catch (err) { toast.error(err?.response?.data?.detail||'Send failed'); }
    finally { setSending(false); }
  };

  const groups = groupByDay(messages);

  return (
    <div className="flex h-screen bg-transparent text-white overflow-hidden">
      {/* ── Chat column ── */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Topbar */}
        <div className="flex items-center gap-3 px-4 sm:px-6 py-3.5 border-b border-white/[0.08] bg-white/[0.02] flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/network/inbox')}
            className="text-[#8a94a6] hover:text-white p-1.5 -ml-1">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0"
            style={{ background:'linear-gradient(135deg,#BF5AF2,#D0BCFF)', color:'white' }}>
            {(otherName||'?')[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-bold text-white text-sm">{otherName||'Conversation'}</p>
              {otherHandle && (
                <button type="button" onClick={() => navigate(`/network/${otherHandle}`)}
                  className="text-[10px] text-[#8a94a6] hover:text-[#62FAE3] transition-colors">@{otherHandle} ↗</button>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#62FAE3] animate-pulse" />
              <span className="text-[10px] text-[#62FAE3]">live · updates every 3s</span>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-5 space-y-1 scroll-smooth">
          {loading ? (
            <div className="flex justify-center pt-20">
              <Loader2 className="w-6 h-6 animate-spin text-[#62FAE3]" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center py-20">
              <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
                <Send className="w-6 h-6 text-[#D0BCFF]/50" />
              </div>
              <p className="text-white font-bold">No messages yet</p>
              <p className="text-[#8a94a6] text-sm">Be the first to say something!</p>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              {groups.map((item, i) => {
                if (item.type === 'date') {
                  return (
                    <div key={`date-${i}`} className="flex items-center gap-3 py-3">
                      <div className="flex-1 h-px bg-white/[0.07]" />
                      <span className="text-[10px] text-[#8a94a6] font-medium">{item.label}</span>
                      <div className="flex-1 h-px bg-white/[0.07]" />
                    </div>
                  );
                }
                const mine = item.sender_id === myId;
                return (
                  <motion.div key={item.id || i}
                    initial={{ opacity:0, y:6, scale:0.97 }} animate={{ opacity:1, y:0, scale:1 }}
                    className={`flex ${mine ? 'justify-end' : 'justify-start'} mb-1`}>
                    <div className={`max-w-[65%] flex flex-col gap-0.5 ${mine ? 'items-end' : 'items-start'}`}>
                      <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${mine ? 'rounded-br-md' : 'rounded-bl-md'}`}
                        style={mine ? {
                          background:'linear-gradient(135deg,#BF5AF2,#62FAE3)',
                          color:'#080d14', fontWeight:500,
                          boxShadow:'0 0 14px rgba(98,250,227,0.12)',
                        } : {
                          background:'rgba(255,255,255,0.06)',
                          border:'1px solid rgba(255,255,255,0.09)',
                          color:'#e2e8f0',
                        }}>
                        {item.content}
                      </div>
                      <span className="text-[10px] text-[#8a94a6] px-1">{fmtTime(item.created_at)}</span>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Composer */}
        <div className="flex items-end gap-2.5 px-4 sm:px-6 py-3.5 border-t border-white/[0.08] bg-white/[0.02] flex-shrink-0">
          <textarea ref={textareaRef} value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Write a message… (Enter ↵ to send, Shift+Enter for new line)"
            rows={1}
            className="flex-1 bg-white/[0.05] border border-white/10 text-white placeholder-[#8a94a6] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#62FAE3]/40 transition-colors resize-none leading-relaxed"
            style={{ minHeight:46, maxHeight:140 }}
          />
          <Button onClick={send} disabled={!text.trim()||sending} size="sm"
            className="w-11 h-11 p-0 rounded-2xl font-bold flex-shrink-0 active:scale-95 transition-transform"
            style={{ background:'linear-gradient(135deg,#D0BCFF,#62FAE3)', boxShadow: text.trim() ? '0 0 16px rgba(98,250,227,0.28)' : 'none' }}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin text-[#080d14]" /> : <Send className="w-4 h-4 text-[#080d14]" />}
          </Button>
        </div>
      </div>

      {/* ── Right sidebar (hidden on small) ── */}
      <div className="hidden xl:flex flex-col gap-4 w-64 flex-shrink-0 border-l border-white/[0.06] p-4 overflow-y-auto">
        {/* Profile mini card */}
        {otherName && (
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 text-center space-y-2">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center font-black text-xl mx-auto"
              style={{ background:'linear-gradient(135deg,#BF5AF2,#D0BCFF)', color:'white' }}>
              {otherName[0]?.toUpperCase()}
            </div>
            <div>
              <p className="font-bold text-white text-sm">{otherName}</p>
              {otherHandle && <p className="text-[11px] text-[#8a94a6]">@{otherHandle}</p>}
            </div>
            {otherHandle && (
              <Button size="sm" variant="outline" onClick={() => navigate(`/network/${otherHandle}`)}
                className="w-full border-white/10 bg-white/[0.04] text-[#c5cbd6] hover:text-white text-xs">
                View Profile ↗
              </Button>
            )}
          </div>
        )}

        {/* Nav links */}
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-3 space-y-0.5">
          <p className="text-[10px] font-bold text-[#8a94a6] uppercase tracking-wider px-2 pb-1">Navigate</p>
          {[
            { label:'All Conversations', path:'/network/inbox', Icon:ChevronRight, col:'#D0BCFF' },
            { label:'Directory', path:'/network', Icon:Users, col:'#62FAE3' },
            { label:'Leaderboard', path:'/network/leaderboard', Icon:Trophy, col:'#f5c842' },
            { label:'My Profile', path:'/network/me', Icon:Sparkles, col:'#D0BCFF' },
          ].map(({ label, path, Icon, col }) => (
            <button key={path} type="button" onClick={() => navigate(path)}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-xl hover:bg-white/[0.05] transition-all text-left">
              <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color:col }} />
              <span className="text-xs text-[#c5cbd6]">{label}</span>
            </button>
          ))}
        </div>

        {/* Keyboard shortcut tip */}
        <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3">
          <p className="text-[10px] text-[#8a94a6] leading-relaxed">
            <span className="text-white font-medium">Enter</span> to send · <span className="text-white font-medium">Shift+Enter</span> for new line
          </p>
        </div>
      </div>
    </div>
  );
};

export default NetworkThreadPage;
